import Link from 'next/link';

import CallOutButton from '../../shared/CallOutButton';
import HeartIcon from '../../icons/Heart';
import useTrainingConnection from '../../../lib/hooks/trainingConnection';
import { useAuth } from '../../../lib/providers/authProvider';
import { useMemo, useRef } from 'react';
import { getResizedImageURL } from '../../../lib/cloudinary';
import Program from 'wwg-utilities/dist/collections/program';
import Photo from 'wwg-utilities/dist/collections/photo';
import Provider from 'wwg-utilities/dist/collections/provider';
import getSalaryStringWithBreak from 'wwg-utilities/dist/sharedFunctions/getSalaryStringWithBreak';
import getProgramCardCostString from 'wwg-utilities/dist/sharedFunctions/getProgramCardCostString';
import getDurationDataString from 'wwg-utilities/dist/sharedFunctions/getDurationDataString';
import DurationData from 'wwg-utilities/dist/types/duration-data';
import paysYouIcon from '../../../lib/config/project/paysYouIcon';
import { isSkillup } from '../../../lib/config/project/project';
import Location from '../../icons/Location';
import getProgramSlug from '../../../lib/data/getProgramSlug';
import GetStartedButton from '../GetStartedButton';
interface TrainingCardProps {
  program: Program;
  pathname?: string;
  shouldFadeOnDelete?: boolean;
}

const TrainingCard: React.FC<TrainingCardProps> = ({
  program,
  pathname,
  shouldFadeOnDelete,
}) => {
  const { seeker } = useAuth();
  const medianAnnualSalary = program.primaryCareerData?.wageData
    ?.medianAnnualSalary?.value as number;
  const programRef = useRef<HTMLDivElement>(null);
  const { connection, handleClick } = useTrainingConnection(
    program,
    seeker,
    programRef,
    shouldFadeOnDelete,
  );
  const trainingImg = useMemo(() => {
    const img = getResizedImageURL(
      (program.image as Photo)?.public_id || '',
      500,
      500,
    );
    return img || '';
  }, [program]);

  const slug = useMemo(() => getProgramSlug(program), [program]);

  const heartFillColor = useMemo(
    () => (isSkillup ? 'fill-pink' : 'fill-heart'),
    [],
  );
  const heartBorderColor = useMemo(
    () => (isSkillup ? 'stroke-pink' : 'stroke-heart'),
    [],
  );

  return (
    <article ref={programRef}>
      <div
        data-testid="program-card"
        className="group relative program-card-wrapper h-auto md:h-[375px] cursor-pointer transition ease-in md:hover:z-[9] duration-75"
      >
        <Link href={`/training${slug}`} shallow={false} className="group">
          <section className="relative w-full h-full overflow-hidden bg-white rounded-md cursor-pointer md:min-h-[375px] program-card shadow-card group-focus:shadow-none">
            <img
              src={trainingImg}
              alt={`${program.name} at ${
                (program.institution as Provider)?.name || program.providerName
              }`}
              className="h-[220px] w-full object-cover rounded-tr-[10px] rounded-tl-[10px]"
            />

            <div className="absolute my-2 top-2 left-2">
              {program.costMerits &&
                program.costMerits?.includes('Pays you') && (
                  <CallOutButton icon={paysYouIcon} label="Pays you!" />
                )}
            </div>
            <section className="flex flex-wrap mx-3 -mt-4 max-w-full overflow-hidden max-h-[32px] relative z-[5]">
              {program.industries?.map((industry: string) => {
                return (
                  <div
                    key={`${industry}-${program?.name}`}
                    className={`${
                      isSkillup ? 'bg-training ' : 'bg-industry'
                    } px-3 py-2 mx-1 text-sm font-medium tracking-wider text-white truncate font-base`}
                  >
                    {industry}
                  </div>
                );
              })}
            </section>
            <section className="relative px-4 pt-0 pb-4 md:min-h-[145px] md:h-[145px] content">
              <h3
                className={`title text-xl font-bold mt-2 md:mb-0 mb-[1%] text-programCardTitle flex-1`}
              >
                {program?.name}
              </h3>
              <section className="flex-1 middle-row">
                <p className="text-sm text-programCardTitle md:line-clamp-1 ">
                  at {((program as Program).institution as Provider)?.name}
                  <br />
                </p>
                <p className="flex items-center text-sm text-programCardTitle ">
                  <Location className="w-3 h-3 fill-black" />
                  &nbsp;
                  <span className="md:line-clamp-1">
                    {program.catalog?.join(', ')}
                  </span>
                </p>
              </section>
              <section className="flex flex-1 overflow-hidden ">
                <p
                  className={`${
                    isSkillup
                      ? 'text-training'
                      : medianAnnualSalary >= 50000
                      ? 'text-primary'
                      : 'text-black'
                  } flex items-center text-left ${
                    program.gradRate ? 'text-xs' : 'text-sm'
                  } font-bold pr-3 border-r-1 leading-4`}
                  dangerouslySetInnerHTML={{
                    __html: getSalaryStringWithBreak(medianAnnualSalary),
                  }}
                ></p>
                <p
                  className={`${
                    isSkillup
                      ? 'text-training'
                      : ((program as Program)?.costData?.costMin || 0) <= 5000
                      ? 'text-primary'
                      : 'text-black'
                  } text-training flex items-center justify-center ${
                    program.gradRate ? 'text-xs' : 'text-sm'
                  } font-bold pr-4 border-r-1 ml-3 leading-4`}
                >
                  {getProgramCardCostString(
                    (program as Program)?.costData?.costMin as number,
                  )}
                </p>
                <p
                  className={`${
                    isSkillup
                      ? 'text-training'
                      : ((program as Program)?.durationData?.timeMin?.days ||
                          0) <= 770
                      ? 'text-primary'
                      : 'text-black'
                  } flex items-center justify-center ${
                    program.gradRate ? 'text-xs' : 'text-sm'
                  } font-bold leading-4 pr-4 ml-3 ${
                    program.gradRate && 'border-r-1'
                  }`}
                >
                  {getDurationDataString(
                    (program as Program)?.durationData as DurationData,
                  )}
                </p>
                {program.gradRate && (
                  <p
                    className={`${
                      isSkillup ? 'text-training' : 'text-primary '
                    } flex items-center text-xs leading-4 font-bold pr-4 ml-3`}
                  >
                    {program.gradRate}%
                    <br />
                    graduate
                  </p>
                )}
              </section>
            </section>
          </section>
        </Link>
        <section className="absolute top-0 right-0">
          <div className="flex items-center justify-between w-full ">
            {pathname === '/profile/my-training' && (
              <div className="relative z-[9] mx-auto">
                <GetStartedButton
                  program={program}
                  location={'profile'}
                  small={true}
                />
              </div>
            )}

            <HeartIcon
              onClick={handleClick}
              className={` ${
                connection && heartFillColor
              } ${heartBorderColor} m-2 h-10 z-[8] relative w-10 cursor-pointer p-2 bg-white rounded-full`}
            />
          </div>
        </section>
      </div>
    </article>
  );
};

export default TrainingCard;
